/**
 * Every endpoint group the pages use. Online-only groups (auth, users, species,
 * export) call the server directly; field records come from records.js, which
 * saves them on the device first and syncs them.
 */
import { api, clearSession, del, get, post, put, refreshAccessToken, stripForUpdate } from './http';
import {
  disturbanceRecords,
  matureTreeRecords,
  quadratRecords,
  regenerationCountRecords,
  regenerationPlotRecords,
  surveyRecords,
  transectRecords,
} from './records';

export { cachedUser, clearSession, getErrorMessage, isNetworkError } from './http';

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const authApi = {
  login: (credentials) => api.post('/auth/login', credentials).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
  changePassword: (currentPassword, newPassword) =>
    api
      .put('/auth/me/password', { current_password: currentPassword, new_password: newPassword })
      .then((r) => r.data),
  refreshToken: refreshAccessToken,
  // Clears stored tokens and fires 'auth:logout'. There is no server-side session to end.
  logout: clearSession,
};

// User management (admin only). These endpoints return { status, users | roles | user }.
export const adminApi = {
  listUsers: () => api.get('/auth/users').then((r) => r.data.users),
  createUser: (userData) => api.post('/auth/users', userData).then((r) => r.data.user),
  resetPassword: (userId, password) =>
    api.put(`/auth/users/${userId}/password`, { password }).then((r) => r.data.user),
  listRoles: () => api.get('/auth/roles').then((r) => r.data.roles),
  setRoles: (userId, roles) => api.put(`/auth/users/${userId}/roles`, { roles }).then((r) => r.data.user),
  setActive: (userId, isActive) =>
    api.put(`/auth/users/${userId}/active`, { is_active: isActive }).then((r) => r.data.user),
};

// Field records are saved on the device first and synced (records.js). Only
// export, which the server builds, goes straight to the network.
export const surveyApi = {
  ...surveyRecords,
  // format 'xlsx' (every table) or 'csv' with a table key; surveyIds limits it (omit for all).
  exportData: ({ format = 'xlsx', table, surveyIds = [] }) => {
    const params = new URLSearchParams({ format });
    if (table) params.set('table', table);
    surveyIds.forEach((id) => params.append('survey', id));
    return api.get('/surveys/export', { params, responseType: 'blob' }).then((r) => r.data);
  },
  // Sharing (online only; owners and admins): who else can see a survey, to view or edit.
  getShares: (id) => api.get(`/surveys/${id}/shares`).then((r) => r.data.data),
  share: (id, userId, access) => put(`/surveys/${id}/shares/${userId}`, { access }),
  unshare: (id, userId) => del(`/surveys/${id}/shares/${userId}`),
  shareCandidates: () => api.get('/survey-share-candidates').then((r) => r.data.data),
};
// Importing a CSV or Excel file into a survey (online only). The file is sent
// both times: once to read its sheets and columns, then with the chosen mapping.
const IMPORT_TIMEOUT = 120000;
const multipart = { headers: { 'Content-Type': 'multipart/form-data' }, timeout: IMPORT_TIMEOUT };
const withFile = (file, fields = {}) => {
  const form = new FormData();
  form.append('file', file);
  Object.entries(fields).forEach(([key, value]) => form.append(key, value));
  return form;
};

export const importApi = {
  // -> { sheets: [{ name, headers, row_count, sample, suggested_type, ready, suggested_mappings }], types }
  // mode 'bulk' reads it for importing whole surveys.
  read: (file, mode) => api.post('/imports/read', withFile(file), { ...multipart, params: { mode } }).then((r) => r.data.data),
  // A blank template: 'xlsx' (every record type) or 'csv' for one type; mode 'bulk' for whole surveys.
  template: (format = 'xlsx', type, mode) =>
    api.get('/imports/template', { params: { format, type, mode }, responseType: 'blob' }).then((r) => r.data),
  // Into one survey. options: { sheet, type, mapping, species_map, dry_run } -> { counts, rows, unknown_species, ... }
  run: (surveyId, file, options) =>
    api.post(`/surveys/${surveyId}/import`, withFile(file, { options: JSON.stringify(options) }), multipart)
      .then((r) => r.data.data),
  // Whole surveys. options: { sheets: [{ sheet, type, mapping }], species_map, dry_run } -> { sheets, counts, ... }
  bulk: (file, options) =>
    api.post('/imports/bulk', withFile(file, { options: JSON.stringify(options) }), multipart).then((r) => r.data.data),
};

export const transectApi = transectRecords;
export const quadratApi = quadratRecords;
export const matureTreeApi = matureTreeRecords;
export const regenerationPlotApi = regenerationPlotRecords;
export const regenerationCountApi = regenerationCountRecords;
export const disturbanceApi = disturbanceRecords;

export const speciesApi = {
  getAll: () => get('/species'),
  // Adds tree_count and regeneration_count to each species.
  getAllWithUsage: () => get('/species', { params: { with_usage: 1 } }),
  getById: (id) => get(`/species/${id}`),
  create: (data) => post('/species', data),
  update: (id, data) => put(`/species/${id}`, stripForUpdate(data)),
  delete: (id) => del(`/species/${id}`),
};

export default api;
